import {
  QueryClient,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, getUserFacingApiMessage } from "@/lib/api-client";
import { clearExpiresAt } from "@/features/auth/lib/token";
import { Sentry } from "@/lib/sentry";
import { recordConnectivityFailure } from "@/hooks/connectivityTracker";
import { useServerStatusStore } from "@/hooks/serverStatusStore";
import { serverStatusKeys } from "@/lib/queries/server-status.queries";
import {
  STALE_TIME_DEFAULT,
  GC_TIME_DEFAULT,
  RETRY_COUNT_DEFAULT,
  RETRY_DELAY_MS,
  REFETCH_ON_WINDOW_FOCUS,
  REDIRECT_GUARD_MS,
  HEALTH_PROBE_MIN_INTERVAL_MS,
} from "@/lib/query-constants";

const AUTH_LOCAL_KEYS = [
  "nexus_user",
  "nexus_tenants",
  "nexus_active_tenant",
  "nexus_csrf",
];

export function clearAuthLocalState(): void {
  clearExpiresAt();
  for (const key of AUTH_LOCAL_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore storage access errors
    }
  }
}

// ─── Augment React Query meta for global error opt-out ──────────────

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
  }
}

interface QueryMeta {
  /** When `true`, the global `QueryCache.onError` toast is suppressed for this query. */
  skipGlobalError?: boolean;
}

interface MutationMeta {
  /** When `true`, the global `MutationCache.onError` toast is suppressed for this mutation. */
  skipGlobalError?: boolean;
}

// ─── 401 redirect guard ─────────────────────────────────────────────

let redirectingAt = 0;

function handleAuthRedirect(): void {
  const now = Date.now();
  if (now - redirectingAt < REDIRECT_GUARD_MS) return;
  redirectingAt = now;
  clearAuthLocalState();
  window.location.href = "/login";
}

// ─── Shared global error handler ────────────────────────────────────

function isTransportError(error: Error): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "OFFLINE" ||
      error.code === "NETWORK_ERROR" ||
      error.code === "TIMEOUT")
  );
}

function isNetworkDisruption(error: Error): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "NETWORK_ERROR" || error.code === "TIMEOUT")
  );
}

let lastHealthProbeAt = 0;

/**
 * On transport failures, count toward confirmed-offline and probe /healthz
 * (debounced) so recovery can still be driven by a successful health poll.
 */
function requestHealthProbe(queryKey?: readonly unknown[]): void {
  if (queryKey?.[0] === serverStatusKeys.all[0]) return;
  if (useServerStatusStore.getState().status === "offline") return;

  const now = Date.now();
  if (now - lastHealthProbeAt < HEALTH_PROBE_MIN_INTERVAL_MS) return;
  lastHealthProbeAt = now;

  void queryClient.refetchQueries({ queryKey: serverStatusKeys.all });
}

/**
 * Classification order (first match wins):
 * 1. 401 → redirect to login (no toast)
 * 2. Offline / network / timeout → skip (ConnectionLostBanner owns outage UX; no toast/Sentry spam)
 * 3. meta.skipGlobalError → skip
 * 4. Health-check query → skip
 * 5. 403 → skip (always handled locally by components)
 * 6. All other errors → toast
 */
function handleGlobalError(
  error: Error,
  meta: Record<string, unknown> | undefined,
  queryKey?: readonly unknown[],
): void {
  if (error instanceof ApiError && error.status === 401) {
    handleAuthRedirect();
    return;
  }

  if (isTransportError(error)) {
    if (isNetworkDisruption(error)) {
      recordConnectivityFailure();
      requestHealthProbe(queryKey);
    }
    return;
  }

  if (meta?.skipGlobalError) {
    return;
  }

  if (queryKey && Array.isArray(queryKey) && queryKey[0] === serverStatusKeys.all[0]) {
    return;
  }

  if (error instanceof ApiError && error.status === 403) {
    return;
  }

  const toastId =
    error instanceof ApiError ? `api-error-${error.status}` : "api-error";
  toast.error(getUserFacingApiMessage(error), { id: toastId });

  Sentry.captureException(error, {
    tags: {
      ...(error instanceof ApiError && { httpStatus: error.status }),
      ...(queryKey && { queryKey: String(queryKey[0]) }),
    },
  });
}

// ─── Status-aware retry function (R16) ──────────────────────────────

/**
 * No retry on 401/403 or client 4xx errors.
 * Limited retry (up to RETRY_COUNT_DEFAULT) on 5xx server errors.
 * No retry on offline/network errors.
 */
function shouldRetry(failureCount: number, error: Error): boolean {
  if (!(error instanceof ApiError)) {
    return failureCount < RETRY_COUNT_DEFAULT;
  }

  if (error.code === "OFFLINE" || error.code === "NETWORK_ERROR") return false;
  if (error.status === 401 || error.status === 403) return false;
  if (error.status >= 400 && error.status < 500) return false;
  if (error.status >= 500) return failureCount < RETRY_COUNT_DEFAULT + 1;

  return failureCount < RETRY_COUNT_DEFAULT;
}

// ─── QueryClient ────────────────────────────────────────────────────

/**
 * Centralized `QueryClient` shared between `QueryClientProvider` and TanStack Router.
 *
 * - Global `QueryCache.onError` handles toasts, 401 redirect, and error classification.
 * - Global `MutationCache.onError` uses the same handler.
 * - Default query options use named constants (see `query-constants.ts`).
 * - Individual queries/mutations can opt out of global error handling via `meta: { skipGlobalError: true }`.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleGlobalError(error, query.meta, query.queryKey);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      handleGlobalError(error, mutation.meta);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_DEFAULT,
      gcTime: GC_TIME_DEFAULT,
      retry: shouldRetry,
      retryDelay: RETRY_DELAY_MS,
      refetchOnWindowFocus: REFETCH_ON_WINDOW_FOCUS,
    },
  },
});
